// Markdown -> EditorJS conversion helpers extracted from the article pages.
// Behavior is pinned by markdownToEditorJS.test.js (characterization snapshots).

export const EDITORJS_VERSION = '2.29.1';

/**
 * Converts worker markdown into an EditorJS document (used by CreateArticle as a
 * fallback when a stored draft is not valid JSON).
 */
export const parseMarkdownToEditorJS = (markdown) => {
  // Featured images now handled by backend worker

  // Dzielimy tekst na linie
  const lines = markdown.split('\n');
  const blocks = [];
  let currentList = null;
  let currentListType = null;

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i].trim();

    // Pomiń puste linie
    if (line === '') {
      // Zakończ obecną listę, jeśli jakaś istnieje
      currentList = null;
      currentListType = null;
      continue;
    }

    // Nagłówek H1 (# Tytuł)
    if (line.startsWith('# ')) {
      blocks.push({
        id: `h1-${Date.now()}-${blocks.length}`,
        type: 'header',
        data: {
          text: line.substring(2),
          level: 1
        }
      });
    }
    // Nagłówek H2 (## Tytuł)
    else if (line.startsWith('## ')) {
      blocks.push({
        id: `h2-${Date.now()}-${blocks.length}`,
        type: 'header',
        data: {
          text: line.substring(3),
          level: 2
        }
      });
    }
    // Nagłówek H3 (### Tytuł)
    else if (line.startsWith('### ')) {
      blocks.push({
        id: `h3-${Date.now()}-${blocks.length}`,
        type: 'header',
        data: {
          text: line.substring(4),
          level: 3
        }
      });
    }
    // Image markdown format: ![alt](url)
    else if (line.match(/!\[(.*?)\]\((.*?)\)/)) {
      const match = line.match(/!\[(.*?)\]\((.*?)\)/);
      const alt = match[1];
      const url = match[2];

      blocks.push({
        id: `image-${Date.now()}-${blocks.length}`,
        type: 'image',
        data: {
          file: {
            url: url,
          },
          caption: alt,
          withBorder: false,
          withBackground: false,
          stretched: false
        }
      });
    }
    // Lista punktowana (- Element)
    else if (line.startsWith('- ')) {
      const listItem = line.substring(2);

      // Jeśli nie istnieje aktualnie lista punktowana, utwórz nową
      if (!currentList || currentListType !== 'unordered') {
        currentList = {
          id: `list-${Date.now()}-${blocks.length}`,
          type: 'list',
          data: {
            style: 'unordered',
            items: [listItem]
          }
        };
        currentListType = 'unordered';
        blocks.push(currentList);
      } else {
        // Dodaj element do istniejącej listy
        currentList.data.items.push(listItem);
      }
    }
    // Lista numerowana (1. Element)
    else if (/^\d+\.\s/.test(line)) {
      const listItem = line.replace(/^\d+\.\s/, '');

      // Jeśli nie istnieje aktualnie lista numerowana, utwórz nową
      if (!currentList || currentListType !== 'ordered') {
        currentList = {
          id: `list-${Date.now()}-${blocks.length}`,
          type: 'list',
          data: {
            style: 'ordered',
            items: [listItem]
          }
        };
        currentListType = 'ordered';
        blocks.push(currentList);
      } else {
        // Dodaj element do istniejącej listy
        currentList.data.items.push(listItem);
      }
    }
    // Separator (---)
    else if (line === '---') {
      blocks.push({
        id: `delimiter-${Date.now()}-${blocks.length}`,
        type: 'delimiter',
        data: {}
      });
    }
    // Standardowy paragraf
    else {
      // Przetwórz pogrubienia (**text** i *text*) na format HTML (<b>text</b>)
      // First handle double asterisks **text** (standard markdown bold)
      let formattedText = line.replace(/\*\*(.*?)\*\*/g, '<b>$1</b>');
      // Then handle single asterisks *text* (alternative bold format)
      // Use negative lookbehind/lookahead to avoid matching asterisks that are part of **text**
      formattedText = formattedText.replace(/(?<!\*)\*([^*]+?)\*(?!\*)/g, '<b>$1</b>');

      blocks.push({
        id: `paragraph-${Date.now()}-${blocks.length}`,
        type: 'paragraph',
        data: {
          text: formattedText
        }
      });
    }
  }

  return {
    time: Date.now(),
    blocks: blocks,
    version: EDITORJS_VERSION
  };
};

/**
 * Minimal markdown -> blocks conversion used by Article.js after legacy generateArticle().
 */
export const parseGeneratedContent = (content) => {
  const lines = content.split('\n');
  const blocks = [];

  lines.forEach(line => {
    line = line.trim();
    if (line.startsWith('# ')) {
      blocks.push({ type: 'header', data: { text: line.substring(2), level: 1 } });
    } else if (line.startsWith('## ')) {
      blocks.push({ type: 'header', data: { text: line.substring(3), level: 2 } });
    } else if (line.startsWith('### ')) {
      blocks.push({ type: 'header', data: { text: line.substring(4), level: 3 } });
    } else if (line.startsWith('- ')) {
      if (blocks.length > 0 && blocks[blocks.length - 1].type === 'list') {
        blocks[blocks.length - 1].data.items.push(line.substring(2));
      } else {
        blocks.push({ type: 'list', data: { style: 'unordered', items: [line.substring(2)] } });
      }
    } else if (line !== '') {
      blocks.push({ type: 'paragraph', data: { text: line } });
    }
  });

  return { blocks };
};
